import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import '../styles/usuarios.css';

const EyeIcon = ({ open }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.77 19.77 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.77 19.77 0 0 1-3.16 4.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const USERS_API = `${API_BASE}/users/operators`;

const ROLE_LABEL = {
  picker: 'Picker Abarrotes',
  frozen: 'Picker Frío',
  checker: 'Checker',
  storage: 'Bodega',
  sales: 'Ventas',
  production: 'Producción',
  scanner: 'Escáner',
  production_closer: 'Cierre Producción',
};

const ROLE_PREFIX = {
  picker: 'p', frozen: 'f', checker: 'c',
  storage: 'b', sales: 'v', production: 'r', scanner: 'e',
  production_closer: 'j',
};

// Roles offered in the "Otros roles" tab (admin/picker/frozen excluded).
const OTHER_ROLES = ['checker', 'storage', 'sales', 'production', 'scanner', 'production_closer'];

const emptyForm = {
  firstName: '',
  lastName: '',
  role: 'picker',
  password: '',
  passwordConfirm: '',
  username: '',
};

// Mirror of backend `_slug`: strip accents, drop non-alphanumeric, lowercase.
const stripAccents = (value) =>
  (value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const slugify = (value) => stripAccents(value).replace(/[^a-z0-9]/g, '');

const buildUsername = (firstName, lastName, role) => {
  const first = slugify(firstName);
  const last = slugify(lastName);
  if (!first || !last) return '';
  const prefix = ROLE_PREFIX[role] || '';
  return `${prefix}${first[0]}${last}`;
};

function PasswordInput({ value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="usuarios-password-wrap">
      <input
        className="usuarios-input usuarios-password-input"
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="usuarios-eye-btn"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      >
        <EyeIcon open={!visible} />
      </button>
    </div>
  );
}

function Usuarios() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [activeTab, setActiveTab] = useState('pickers'); // 'pickers' | 'otros'

  const [form, setForm] = useState(emptyForm);
  const [showUsernameField, setShowUsernameField] = useState(false);
  const [createMessage, setCreateMessage] = useState('');

  const [otherForm, setOtherForm] = useState({
    firstName: '', lastName: '', role: 'checker',
    password: '', passwordConfirm: '', username: '',
  });
  const [showOtherUsername, setShowOtherUsername] = useState(false);
  const [otherMessage, setOtherMessage] = useState('');
  const updateOther = (field) => (e) =>
    setOtherForm((prev) => ({ ...prev, [field]: e.target.value }));

  const [resetTargetId, setResetTargetId] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const [search, setSearch] = useState('');

  const authHeaders = useCallback(
    () => ({
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const res = await fetch(`${USERS_API}/`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      setListError(`No se pudieron cargar los usuarios: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateForm = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const resetCreateForm = () => {
    setForm(emptyForm);
    setShowUsernameField(false);
  };

  const handleCreate = async () => {
    setCreateMessage('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setCreateMessage('Nombre y apellido son requeridos.');
      return;
    }
    if (!form.password || form.password.length < 8) {
      setCreateMessage('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setCreateMessage('Las contraseñas no coinciden.');
      return;
    }

    const body = {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      role: form.role,
      password: form.password,
      password_confirm: form.passwordConfirm,
    };
    if (showUsernameField && form.username.trim()) {
      body.username = form.username.trim();
    }

    try {
      const res = await fetch(`${USERS_API}/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        setCreateMessage(`Usuario ${data.username} creado.`);
        resetCreateForm();
        fetchUsers();
        return;
      }

      if (res.status === 409 && data.error === 'username_taken') {
        setShowUsernameField(true);
        setForm((prev) => ({ ...prev, username: data.suggested || '' }));
        setCreateMessage(
          `El nombre de usuario "${data.suggested}" ya existe. Edita el campo y vuelve a enviar.`
        );
        return;
      }

      setCreateMessage(data.error || 'No se pudo crear el usuario.');
    } catch (err) {
      setCreateMessage(`Error: ${err.message}`);
    }
  };

  const handleCreateOther = async () => {
    setOtherMessage('');
    if (!otherForm.firstName.trim() || !otherForm.lastName.trim()) {
      setOtherMessage('Nombre y apellido son requeridos.');
      return;
    }
    if (!otherForm.password || otherForm.password.length < 8) {
      setOtherMessage('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (otherForm.password !== otherForm.passwordConfirm) {
      setOtherMessage('Las contraseñas no coinciden.');
      return;
    }
    const body = {
      first_name: otherForm.firstName.trim(),
      last_name: otherForm.lastName.trim(),
      role: otherForm.role,
      password: otherForm.password,
      password_confirm: otherForm.passwordConfirm,
    };
    if (showOtherUsername && otherForm.username.trim()) {
      body.username = otherForm.username.trim();
    }
    try {
      const res = await fetch(`${USERS_API}/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 201) {
        setOtherMessage(`Usuario ${data.username} creado.`);
        setOtherForm({
          firstName: '', lastName: '', role: otherForm.role,
          password: '', passwordConfirm: '', username: '',
        });
        setShowOtherUsername(false);
        fetchUsers();
        return;
      }
      if (res.status === 409 && data.error === 'username_taken') {
        setShowOtherUsername(true);
        setOtherForm((prev) => ({ ...prev, username: data.suggested || '' }));
        setOtherMessage(
          `El nombre "${data.suggested}" ya existe. Edítalo y reenvía.`
        );
        return;
      }
      setOtherMessage(data.error || 'No se pudo crear el usuario.');
    } catch (err) {
      setOtherMessage(`Error: ${err.message}`);
    }
  };

  const handleResetPassword = async (userId) => {
    setResetMessage('');
    if (!resetPwd || resetPwd.length < 8) {
      setResetMessage('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (resetPwd !== resetPwdConfirm) {
      setResetMessage('Las contraseñas no coinciden.');
      return;
    }
    try {
      const res = await fetch(`${USERS_API}/${userId}/reset-password/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          password: resetPwd,
          password_confirm: resetPwdConfirm,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setResetTargetId(null);
        setResetPwd('');
        setResetPwdConfirm('');
        setResetMessage('');
        alert('Contraseña actualizada.');
      } else {
        setResetMessage(data.error || 'No se pudo actualizar la contraseña.');
      }
    } catch (err) {
      setResetMessage(`Error: ${err.message}`);
    }
  };

  const handleSwitchRole = async (user) => {
    const target = user.role === 'picker' ? 'frozen' : 'picker';
    const ok = window.confirm(
      `¿Cambiar a ${user.first_name} ${user.last_name} a ${ROLE_LABEL[target]}? El nombre de usuario cambiará a empezar con "${target === 'picker' ? 'p' : 'f'}".`
    );
    if (!ok) return;
    try {
      const res = await fetch(`${USERS_API}/${user.id}/switch-role/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ role: target }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchUsers();
      } else if (res.status === 409) {
        alert(
          `No se pudo cambiar el rol: ya existe un usuario con el nombre "${data.suggested}". Desactívalo primero.`
        );
      } else {
        alert(data.error || 'No se pudo cambiar el rol.');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeactivate = async (user) => {
    const ok = window.confirm(
      `¿Desactivar a ${user.first_name} ${user.last_name} (${user.username})? No podrá iniciar sesión.`
    );
    if (!ok) return;
    try {
      const res = await fetch(`${USERS_API}/${user.id}/deactivate/`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        fetchUsers();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'No se pudo desactivar el usuario.');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const searchNeedle = stripAccents(search.trim());
  const matchesSearch = (user) => {
    if (!searchNeedle) return true;
    const haystack = stripAccents(
      `${user.first_name} ${user.last_name} ${user.username}`
    );
    return haystack.includes(searchNeedle);
  };

  const pickers = users.filter((u) => u.role === 'picker' && matchesSearch(u));
  const frozens = users.filter((u) => u.role === 'frozen' && matchesSearch(u));

  const otherUsers = users.filter(
    (u) => OTHER_ROLES.includes(u.role) && matchesSearch(u)
  );
  const otherUsersByRole = OTHER_ROLES.map((role) => ({
    role,
    label: ROLE_LABEL[role],
    list: otherUsers.filter((u) => u.role === role),
  })).filter((g) => g.list.length > 0);

  const usernamePreview = showUsernameField
    ? form.username
    : buildUsername(form.firstName, form.lastName, form.role);

  const renderUserRow = (user) => (
    <li key={user.id} className="usuarios-row">
      <div className="usuarios-row-info">
        <strong>
          {user.first_name} {user.last_name}
        </strong>
        <span className="usuarios-username">@{user.username}</span>
      </div>
      <div className="usuarios-row-actions">
        <button
          className="usuarios-action-btn"
          onClick={() => {
            setResetTargetId(resetTargetId === user.id ? null : user.id);
            setResetPwd('');
            setResetPwdConfirm('');
            setResetMessage('');
          }}
        >
          Cambiar contraseña
        </button>
        {(user.role === 'picker' || user.role === 'frozen') && (
          <button
            className="usuarios-action-btn"
            onClick={() => handleSwitchRole(user)}
          >
            Cambiar a {user.role === 'picker' ? 'Frío' : 'Abarrotes'}
          </button>
        )}
        <button
          className="usuarios-action-btn usuarios-danger"
          onClick={() => handleDeactivate(user)}
        >
          Desactivar
        </button>
      </div>
      {resetTargetId === user.id && (
        <div className="usuarios-reset-box">
          <PasswordInput
            placeholder="Nueva contraseña"
            value={resetPwd}
            onChange={(e) => setResetPwd(e.target.value)}
          />
          <PasswordInput
            placeholder="Repite la contraseña"
            value={resetPwdConfirm}
            onChange={(e) => setResetPwdConfirm(e.target.value)}
          />
          <button
            className="usuarios-action-btn"
            onClick={() => handleResetPassword(user.id)}
          >
            Guardar contraseña
          </button>
          {resetMessage && <p className="usuarios-message">{resetMessage}</p>}
        </div>
      )}
    </li>
  );

  return (
    <div className="usuarios-page">
      <button
        className="adminproductos-back-button"
        onClick={() => navigate('/init')}
      >
        &#8592;
      </button>

      <div className="usuarios-container">
        <h1 className="usuarios-title">Usuarios</h1>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'pickers' ? 'active' : ''}`}
            onClick={() => setActiveTab('pickers')}
          >
            Pickers
          </button>
          <button
            className={`tab ${activeTab === 'otros' ? 'active' : ''}`}
            onClick={() => setActiveTab('otros')}
          >
            Otros roles
          </button>
        </div>

        <div className="usuarios-card">
          <label className="usuarios-label">Buscar usuario</label>
          <input
            className="usuarios-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nombre, apellido o usuario"
          />
        </div>

        {activeTab === 'pickers' && (
          <>
        <div className="usuarios-card">
          <h2 className="usuarios-subtitle">Crear nuevo usuario</h2>
          <label className="usuarios-label">Nombre</label>
          <input
            className="usuarios-input"
            type="text"
            value={form.firstName}
            onChange={updateForm('firstName')}
            placeholder="Ej: Ana"
          />
          <label className="usuarios-label">Apellido</label>
          <input
            className="usuarios-input"
            type="text"
            value={form.lastName}
            onChange={updateForm('lastName')}
            placeholder="Ej: Pérez"
          />
          <label className="usuarios-label">Rol</label>
          <div className="usuarios-role-row">
            <label className="usuarios-radio">
              <input
                type="radio"
                name="role"
                value="picker"
                checked={form.role === 'picker'}
                onChange={updateForm('role')}
              />
              Picker Abarrotes
            </label>
            <label className="usuarios-radio">
              <input
                type="radio"
                name="role"
                value="frozen"
                checked={form.role === 'frozen'}
                onChange={updateForm('role')}
              />
              Picker Frío
            </label>
          </div>
          <label className="usuarios-label">Contraseña</label>
          <PasswordInput
            value={form.password}
            onChange={updateForm('password')}
          />
          <label className="usuarios-label">Repetir contraseña</label>
          <PasswordInput
            value={form.passwordConfirm}
            onChange={updateForm('passwordConfirm')}
          />
          {showUsernameField && (
            <>
              <label className="usuarios-label">
                Nombre de usuario (editable porque ya existe uno)
              </label>
              <input
                className="usuarios-input"
                type="text"
                value={form.username}
                onChange={updateForm('username')}
              />
            </>
          )}
          {usernamePreview && (
            <p className="usuarios-preview">
              Nombre de usuario: <strong>{usernamePreview}</strong>
            </p>
          )}
          <button className="usuarios-primary-btn" onClick={handleCreate}>
            Crear
          </button>
          {createMessage && <p className="usuarios-message">{createMessage}</p>}
        </div>

        <div className="usuarios-card">
          <h2 className="usuarios-subtitle">Picker Abarrotes</h2>
          {loading && <p>Cargando…</p>}
          {listError && <p className="usuarios-message">{listError}</p>}
          {!loading && pickers.length === 0 && <p>Sin pickers activos.</p>}
          <ul className="usuarios-list">{pickers.map(renderUserRow)}</ul>
        </div>

        <div className="usuarios-card">
          <h2 className="usuarios-subtitle">Picker Frío</h2>
          {!loading && frozens.length === 0 && <p>Sin pickers de frío activos.</p>}
          <ul className="usuarios-list">{frozens.map(renderUserRow)}</ul>
        </div>
          </>
        )}

        {activeTab === 'otros' && (
          <>
            <div className="usuarios-card">
              <h2 className="usuarios-subtitle">Crear usuario (otros roles)</h2>
              <label className="usuarios-label">Nombre</label>
              <input
                className="usuarios-input"
                type="text"
                value={otherForm.firstName}
                onChange={updateOther('firstName')}
                placeholder="Ej: Ana"
              />
              <label className="usuarios-label">Apellido</label>
              <input
                className="usuarios-input"
                type="text"
                value={otherForm.lastName}
                onChange={updateOther('lastName')}
                placeholder="Ej: Pérez"
              />
              <label className="usuarios-label">Rol</label>
              <select
                className="usuarios-input"
                value={otherForm.role}
                onChange={updateOther('role')}
              >
                {OTHER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
              <label className="usuarios-label">Contraseña</label>
              <PasswordInput
                value={otherForm.password}
                onChange={updateOther('password')}
              />
              <label className="usuarios-label">Repetir contraseña</label>
              <PasswordInput
                value={otherForm.passwordConfirm}
                onChange={updateOther('passwordConfirm')}
              />
              {showOtherUsername && (
                <>
                  <label className="usuarios-label">
                    Nombre de usuario (editable porque ya existe uno)
                  </label>
                  <input
                    className="usuarios-input"
                    type="text"
                    value={otherForm.username}
                    onChange={updateOther('username')}
                  />
                </>
              )}
              {(showOtherUsername
                ? otherForm.username
                : buildUsername(
                    otherForm.firstName,
                    otherForm.lastName,
                    otherForm.role
                  )) && (
                <p className="usuarios-preview">
                  Nombre de usuario:{' '}
                  <strong>
                    {showOtherUsername
                      ? otherForm.username
                      : buildUsername(
                          otherForm.firstName,
                          otherForm.lastName,
                          otherForm.role
                        )}
                  </strong>
                </p>
              )}
              <button className="usuarios-primary-btn" onClick={handleCreateOther}>
                Crear
              </button>
              {otherMessage && (
                <p className="usuarios-message">{otherMessage}</p>
              )}
            </div>

            {otherUsersByRole.map((group) => (
              <div className="usuarios-card" key={group.role}>
                <h2 className="usuarios-subtitle">{group.label}</h2>
                <ul className="usuarios-list">
                  {group.list.map(renderUserRow)}
                </ul>
              </div>
            ))}
            {otherUsersByRole.length === 0 && !loading && (
              <p>Sin usuarios de otros roles.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Usuarios;
